/**
 * Realtime room subscriptions — Track C step **C1.4a–C1.4f**.
 *
 * `main.ts` has always opened a socket and handled `list_update`/`doc_update`,
 * but the client never joined a room, so the server had nowhere to publish to
 * and the handlers were dead code (`CACHE_AUDIT.md` C-03).
 *
 * ## The verified wire protocol — note it is *not* `list_subscribe`
 *
 * - A **list** joins the doctype room `doctype:<DocType>` by emitting
 *   `doctype_subscribe` (`doctype`) and leaves with `doctype_unsubscribe`.
 *   `list_update` is published to exactly that room:
 *   `frappe/realtime.py:52-53` (`room = get_doctype_room(doctype)`) and
 *   `:146-147` (`f"doctype:{doctype}"`), from
 *   `frappe/model/document.py:1231` (`notify_update`, on every non-read-only,
 *   non-single, non-childtable ORM save, after commit). The server handler is
 *   `realtime/handlers/frappe_handlers.js:22-29`.
 * - A **doc** joins `doc:<DocType>/<name>` by emitting
 *   `doc_subscribe` (`doctype`, `docname`) and leaves with `doc_unsubscribe`
 *   (`frappe_handlers.js:48-62`; rooms at `frappe/realtime.py:150-151`).
 *   `doc_update` is published to that room from `document.py:1219-1226`.
 *
 * There is **no `list_subscribe` / `list_unsubscribe` event anywhere in this
 * Frappe** (`grep -rn "list_subscribe" ~/frappe-bench/apps/frappe` → no hits),
 * and the installed frappe-ui emits `doctype_subscribe` as well
 * (`node_modules/frappe-ui/src/resources/realtime.ts:19`). `PLAN_TRACK_C.md`
 * C1.4a/C1.4c name `list_subscribe`; emitting it would open no room and
 * reproduce the very dead-handler bug this step fixes. The verified event name
 * is implemented instead — see the PR body.
 *
 * A subscription is **ref-counted per room** (C1.4c): two mounted
 * `Salon Appointment` lists emit one `doctype_subscribe`, and only the last
 * unmount emits the matching unsubscribe.
 *
 * Everything here is a no-op when the socket failed to initialise
 * (`main.ts` leaves `$socket = null`): the app degrades to refetch-only and
 * never throws (C1.4f).
 */

import { onMounted, onUnmounted, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { queryClient } from './useQueryClient'
import { realtimeDocKeys, realtimeMap } from './realtimeMap'

/** The slice of socket.io's client that this module uses. */
export interface RealtimeSocket {
  emit: (event: string, ...args: unknown[]) => void
  on: (event: string, handler: (payload: any) => void) => void
}

function socket(): RealtimeSocket | null {
  const candidate = window.__glamora_socket as RealtimeSocket | null | undefined
  return candidate && typeof candidate.emit === 'function' ? candidate : null
}

interface Room {
  /** How many mounted callers hold this room. */
  count: number
  doctype: string
  /** Present for a doc room; absent for a doctype (list) room. */
  name?: string
}

/** Room key → live subscriber count. Keyed by `<doctype>` or `<doctype>/<name>`. */
const rooms = new Map<string, Room>()

function roomKey(doctype: string, name?: string): string {
  return name ? `${doctype}/${name}` : doctype
}

/** Emit the protocol's join for a room. */
function emitJoin(doctype: string, name?: string): void {
  const s = socket()
  if (!s) return
  if (name) s.emit('doc_subscribe', doctype, name)
  else s.emit('doctype_subscribe', doctype)
}

/** Emit the protocol's leave for a room. */
function emitLeave(doctype: string, name?: string): void {
  const s = socket()
  if (!s) return
  if (name) s.emit('doc_unsubscribe', doctype, name)
  else s.emit('doctype_unsubscribe', doctype)
}

/**
 * Join a room, or add a reference to the one already joined.
 * The returned function is idempotent and releases exactly one reference.
 */
function acquire(doctype: string, name?: string): () => void {
  const key = roomKey(doctype, name)
  const live = rooms.get(key)
  if (live) {
    live.count += 1
  } else {
    rooms.set(key, { count: 1, doctype, name })
    emitJoin(doctype, name)
  }

  let released = false
  return () => {
    if (released) return
    released = true
    const room = rooms.get(key)
    if (!room) return // cleared by a lifecycle clear; nothing left to leave
    room.count -= 1
    if (room.count > 0) return
    rooms.delete(key)
    emitLeave(doctype, name)
  }
}

/** Join the doctype room for `doctype` (a list query). Returns the release fn. */
export function subscribeList(doctype: string): () => void {
  return acquire(doctype)
}

/** Join the doc room for one document (a doc query). Returns the release fn. */
export function subscribeDoc(doctype: string, name: string): () => void {
  return acquire(doctype, name)
}

/**
 * The composable form `useRpcQuery` calls: join on mount, leave on unmount
 * (C1.4c).
 *
 * `doctype`/`name` may be reactive. A doc query whose `name` changes with the
 * key (a detail screen moving from one record to another) must move its room
 * too, or it would keep listening to the document it no longer shows.
 */
export function useRealtime(
  doctype: MaybeRefOrGetter<string>,
  name?: MaybeRefOrGetter<string | undefined>,
): void {
  let release: (() => void) | null = null

  function arm(): void {
    release?.()
    release = null
    const kind = toValue(doctype)
    if (!kind) return
    const docName = name === undefined ? undefined : toValue(name)
    release = acquire(kind, docName || undefined)
  }

  onMounted(arm)
  onUnmounted(() => {
    release?.()
    release = null
  })
  watch(
    [() => toValue(doctype), () => (name === undefined ? undefined : toValue(name))],
    arm,
  )
}

/** Every room currently held, for a reconnect or a lifecycle clear. */
export function activeRooms(): string[] {
  return [...rooms.keys()]
}

/**
 * Re-emit every held join. A reconnect has dropped every room on the server,
 * so each ref-counted subscription must be re-armed (C1.4f).
 */
export function rearmRealtime(): void {
  for (const room of rooms.values()) emitJoin(room.doctype, room.name)
}

/**
 * Leave every room and forget the ref-counts. Called on logout, session
 * expiry, impersonation end and a company switch, beside
 * `queryClient.clear()` (C1.4f, C2.5).
 *
 * Mounted pages that release later are harmless: their room entry is gone, so
 * the release is a no-op and no stray unsubscribe is emitted.
 */
export function clearRealtime(): void {
  for (const room of rooms.values()) emitLeave(room.doctype, room.name)
  rooms.clear()
}

/**
 * Does `key` begin with the whole `prefix`? The `predicate` behind C1.4d's
 * "invalidate what this doctype published to".
 */
export function keyStartsWith(
  key: readonly unknown[],
  prefix: readonly unknown[],
): boolean {
  if (prefix.length > key.length) return false
  return prefix.every((part, index) => key[index] === part)
}

/** C1.4d — a `list_update` refreshes only the queries the doctype maps to. */
export function invalidateDoctype(doctype: string | undefined): void {
  if (!doctype) return
  const prefixes = realtimeMap[doctype]
  if (!prefixes) {
    if (import.meta.env.DEV) {
      console.debug('[realtime] list_update for an unmapped doctype', doctype)
    }
    return
  }
  for (const prefix of prefixes) {
    void queryClient.invalidateQueries({
      predicate: (query) => keyStartsWith(query.queryKey, prefix),
    })
  }
}

/** C1.4e — a `doc_update` refreshes that document's own key, never a domain. */
export function invalidateDoc(
  doctype: string | undefined,
  name: string | undefined,
): void {
  if (!doctype || !name) return
  const docKey = realtimeDocKeys[doctype]
  if (!docKey) {
    if (import.meta.env.DEV) {
      console.debug('[realtime] doc_update with no cached doc key', doctype, name)
    }
    return
  }
  void queryClient.invalidateQueries({ queryKey: docKey(name) })
}

/**
 * Wire the socket into the query cache, once. Called from `main.ts` right after
 * the socket is created — the handlers live here, not there, so "where is the
 * handler?" has exactly one answer (C1.4b).
 */
export function attachRealtime(): void {
  const s = socket()
  if (!s) return
  s.on('connect', () => rearmRealtime())
  s.on('list_update', (data: { doctype?: string } | undefined) => {
    invalidateDoctype(data?.doctype)
  })
  s.on('doc_update', (data: { doctype?: string; name?: string } | undefined) => {
    invalidateDoc(data?.doctype, data?.name)
  })
}
